import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Network, RefreshCw, ArrowLeft, CheckCircle2, AlertTriangle,
  Loader2, ShieldCheck, Clock, Database, Info, BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import ScimProvisioningCard from "../components/ScimProvisioningCard";

/**
 * IdentityFederationPage
 * ──────────────────────
 * BCM-Manager-scoped UI over the existing /api/federation/* backend
 * (routers/federation.py). Surfaces:
 *   • Configured sources (M365 / Workday / ServiceNow) and their per-tenant
 *     last-sync timestamp.
 *   • A target-collection selector (resources / executives / vendors /
 *     asset_owners) — the same options the backend accepts.
 *   • One-click "Sync now" per source AND a "Sync all configured" button.
 *
 * IMPORTANT: this page intentionally NEVER asks for IdP credentials in the
 * UI. Per the federation playbook, secrets live in backend env vars only.
 * If a source is "Not configured", we explain what env keys the platform
 * admin needs to set and show the operations team the documented
 * Federation Request Package URL.
 */

const TARGET_OPTIONS = [
  { value: "resources",    label: "Resources (people)" },
  { value: "executives",   label: "Executives" },
  { value: "vendors",      label: "Vendors" },
  { value: "asset_owners", label: "Asset Owners" },
];

const SOURCE_REQUIRED_ENV = {
  o365:       ["O365_TENANT_ID", "O365_CLIENT_ID", "O365_CLIENT_SECRET"],
  ad:         ["AD_LDAP_HOST", "AD_BIND_DN", "AD_BIND_PASSWORD", "AD_USER_BASE_DN"],
  workday:    ["WORKDAY_HOST", "WORKDAY_TENANT", "WORKDAY_CLIENT_ID", "WORKDAY_CLIENT_SECRET", "WORKDAY_REFRESH_TOKEN"],
  servicenow: ["SERVICENOW_INSTANCE", "SERVICENOW_USER", "SERVICENOW_PASS"],
};

const TEST_BIND_LABEL = {
  ad:         { idle: "Test AD bind",         busy: "Probing AD bind…" },
  o365:       { idle: "Test M365 connection", busy: "Probing M365 token…" },
  workday:    { idle: "Test Workday connection", busy: "Probing Workday token…" },
  servicenow: { idle: "Test ServiceNow connection", busy: "Probing ServiceNow auth…" },
};

function formatTimestamp(iso) {
  if (!iso) return "Never";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function IdentityFederationPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState("resources");
  const [syncing, setSyncing] = useState(null); // sourceId | "all" | null
  const [lastResult, setLastResult] = useState(null);
  const [bindTesting, setBindTesting] = useState(null); // sourceId | null
  const [bindResult, setBindResult] = useState(null);   // { ok, error, ... }

  const canSync = ["admin", "bcm_manager", "department_head"].includes(user?.role);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get("/federation/status");
      setStatus(res.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to load federation status"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const triggerSync = async (sourceId) => {
    if (!canSync) {
      toast.error("Only BCM Managers, Department Heads or platform Admins can trigger a sync");
      return;
    }
    setSyncing(sourceId || "all");
    try {
      const body = { target_collection: target };
      if (sourceId) body.source = sourceId;
      const res = await api.post("/federation/sync-now", body);
      setLastResult({ sourceId: sourceId || "all", ...res.data });
      const summary = res.data?.summary || {};
      const totalCreated = Object.values(summary).reduce((a, s) => a + (s.created || 0), 0);
      const totalUpdated = Object.values(summary).reduce((a, s) => a + (s.updated || 0), 0);
      const totalFetched = Object.values(summary).reduce((a, s) => a + (s.fetched || 0), 0);
      if (totalFetched === 0) {
        toast.info("Sync ran, but the source returned 0 records. Check that the source credentials are configured.");
      } else {
        toast.success(`Synced — ${totalFetched} fetched · ${totalCreated} created · ${totalUpdated} updated`);
      }
      await loadStatus();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Sync failed"));
    } finally {
      setSyncing(null);
    }
  };

  const triggerBindTest = async (sourceId) => {
    if (!canSync) {
      toast.error("Only BCM Managers, Department Heads or platform Admins can run a test bind");
      return;
    }
    setBindTesting(sourceId);
    setBindResult(null);
    try {
      const res = await api.post("/federation/test-bind", { source: sourceId });
      setBindResult({ sourceId, ...res.data });
      if (res.data?.ok) {
        toast.success(res.data.note || "Bind successful");
      } else {
        toast.error(res.data?.error || "Bind probe failed");
      }
    } catch (e) {
      const msg = extractErrorMessage(e, "Test bind failed");
      setBindResult({ sourceId, ok: false, error: msg });
      toast.error(msg);
    } finally {
      setBindTesting(null);
    }
  };

  const sources = status?.sources || [];
  const configuredCount = sources.filter((s) => s.configured).length;
  const allConfigured = sources.length > 0 && configuredCount === sources.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-4 sm:p-8" data-testid="identity-federation-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-2"
              data-testid="federation-back"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h1
              className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              <Network className="w-7 h-7 text-indigo-700" />
              Connect Your Systems
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-3xl">
              Link Microsoft 365, Active Directory, Workday or ServiceNow to bring in people and
              structure. Optional — you can use the platform without connecting any system.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[0.7rem]">
              <ShieldCheck className="w-3 h-3 mr-1" /> BCM Manager / Admin
            </Badge>
            <Button
              onClick={loadStatus}
              variant="outline"
              disabled={loading}
              data-testid="federation-refresh"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
          </div>
        </div>

        {/* Activation runbook banner — surfaces the Runbook the moment any source needs activation. Auto-hides once all sources are configured. */}
        {!loading && sources.length > 0 && !allConfigured && (
          <Card
            className="mb-5 border-indigo-300 bg-gradient-to-r from-indigo-50 via-violet-50 to-white"
            data-testid="federation-runbook-banner"
          >
            <CardContent className="p-4 flex flex-wrap items-start gap-3">
              <BookOpen className="w-5 h-5 text-indigo-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-800">
                  First time activating federation? Follow the step-by-step Runbook.
                </p>
                <p className="text-xs text-slate-600 mt-0.5 leading-snug">
                  At least one source still shows <span className="font-semibold text-amber-700">Not configured</span>.
                  The Federation Activation Runbook walks your CS engineer through every Emergent click —
                  setting env-vars, redeploying, and verifying with the Test buttons below. It also
                  covers per-source troubleshooting and the Demo / Production sync.
                </p>
              </div>
              <a
                href="/Federation_Activation_Runbook.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-700 hover:bg-indigo-800 text-white text-xs font-semibold rounded-md transition-colors flex-shrink-0"
                data-testid="federation-runbook-link"
              >
                <BookOpen className="w-4 h-4" />
                Open Runbook
              </a>
            </CardContent>
          </Card>
        )}

        {/* Status strip */}
        <Card className="mb-5 border-indigo-200 bg-gradient-to-r from-indigo-50 to-white">
          <CardContent className="p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <div className="flex items-center gap-2">
              {allConfigured
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                : <AlertTriangle className="w-4 h-4 text-amber-600" />}
              <span className="font-semibold text-slate-700">Sources configured:</span>
              <span className="text-slate-600">{configuredCount} / {sources.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-slate-500" />
              <span className="font-semibold text-slate-700">Mergeable fields:</span>
              <span className="text-slate-600 text-xs">
                {(status?.mergeable_fields || []).join(", ") || "—"}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Target collection selector + Sync All */}
        <Card className="mb-5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="w-5 h-5 text-slate-500" /> Target Collection
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[260px]">
              <Select value={target} onValueChange={setTarget} data-testid="federation-target-select">
                <SelectTrigger className="w-full" data-testid="federation-target-trigger">
                  <SelectValue placeholder="Select target collection" />
                </SelectTrigger>
                <SelectContent>
                  {TARGET_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`federation-target-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1.5">
                Federation upserts on <code className="bg-slate-100 rounded px-1">email</code> and
                only fills missing fields on the matching record in this collection.
              </p>
            </div>
            <Button
              onClick={() => triggerSync(null)}
              disabled={!canSync || syncing !== null || configuredCount === 0}
              className="bg-indigo-700 hover:bg-indigo-800 text-white"
              data-testid="federation-sync-all"
            >
              {syncing === "all"
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Syncing all…</>
                : <><RefreshCw className="w-4 h-4 mr-2" /> Sync all configured</>}
            </Button>
          </CardContent>
        </Card>

        {/* Sources */}
        {loading ? (
          <div className="text-center py-16 text-slate-500" data-testid="federation-loading">
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
            Loading federation status…
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="federation-sources-grid">
            {sources.map((s) => {
              const isConfigured = !!s.configured;
              const isThisSyncing = syncing === s.id;
              return (
                <Card
                  key={s.id}
                  className={isConfigured ? "border-emerald-200" : "border-slate-200"}
                  data-testid={`federation-source-${s.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-800 leading-snug">{s.name}</p>
                        <code className="text-[0.65rem] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 inline-block mt-1">
                          {s.id}
                        </code>
                      </div>
                      {isConfigured ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[0.65rem] flex-shrink-0">
                          Configured
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[0.65rem] flex-shrink-0">
                          Not configured
                        </Badge>
                      )}
                    </div>

                    <div className="text-xs text-slate-500 flex items-center gap-1.5 mb-3">
                      <Clock className="w-3 h-3" />
                      Last sync: <span className="font-semibold text-slate-700">{formatTimestamp(s.last_sync_at)}</span>
                    </div>

                    {!isConfigured && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3 text-[0.7rem] text-amber-900 leading-snug">
                        <div className="flex items-start gap-1.5">
                          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-semibold mb-1">Required backend env vars:</p>
                            <code className="bg-white border border-amber-200 rounded px-1 py-0.5 break-all">
                              {(SOURCE_REQUIRED_ENV[s.id] || []).join(", ") || "—"}
                            </code>
                            <p className="mt-1.5">
                              Use the <em>Federation Request Package</em> guidebook to collect
                              these from the customer's IT lead, then set them on the backend
                              and restart the service.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={() => triggerSync(s.id)}
                      disabled={!canSync || syncing !== null || !isConfigured}
                      variant={isConfigured ? "default" : "outline"}
                      className={isConfigured ? "bg-indigo-700 hover:bg-indigo-800 text-white w-full" : "w-full"}
                      data-testid={`federation-sync-${s.id}`}
                    >
                      {isThisSyncing
                        ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Syncing…</>
                        : <><RefreshCw className="w-3.5 h-3.5 mr-2" /> Sync now</>}
                    </Button>

                    {/* Per-source test-connection probe (AD = LDAP bind,
                        M365 = OAuth2 token mint, Workday = OAuth2
                        refresh_token grant + /workers read, ServiceNow
                        = Basic-Auth GET on sys_user). 5-second probe so
                        the IT lead can validate wiring BEFORE running a
                        full sync. */}
                    {TEST_BIND_LABEL[s.id] && (
                      <>
                        <Button
                          onClick={() => triggerBindTest(s.id)}
                          disabled={!canSync || bindTesting !== null || !isConfigured}
                          variant="outline"
                          size="sm"
                          className="w-full mt-2"
                          data-testid={`federation-test-bind-${s.id}`}
                        >
                          {bindTesting === s.id
                            ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> {TEST_BIND_LABEL[s.id].busy}</>
                            : <><ShieldCheck className="w-3.5 h-3.5 mr-2" /> {TEST_BIND_LABEL[s.id].idle}</>}
                        </Button>
                        {bindResult && bindResult.sourceId === s.id && (
                          <div
                            className={
                              "mt-2 rounded-lg p-2.5 text-[0.7rem] leading-snug border " +
                              (bindResult.ok
                                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                                : "bg-rose-50 border-rose-200 text-rose-900")
                            }
                            data-testid={`federation-bind-result-${bindResult.ok ? "ok" : "fail"}`}
                          >
                            <div className="flex items-start gap-1.5">
                              {bindResult.ok
                                ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
                              <div className="min-w-0">
                                <p className="font-semibold mb-1">
                                  {bindResult.ok ? "Probe successful" : `Failed at: ${bindResult.stage || "unknown"}`}
                                </p>
                                <p className="break-words">
                                  {bindResult.ok ? bindResult.note : bindResult.error}
                                </p>
                                {bindResult.ok && bindResult.base_dn && (
                                  <p className="mt-1 text-[0.65rem] opacity-80">
                                    Base&nbsp;DN: <code className="break-all">{bindResult.base_dn}</code>
                                  </p>
                                )}
                                {bindResult.ok && bindResult.tenant && (
                                  <p className="mt-1 text-[0.65rem] opacity-80">
                                    Tenant: <code className="break-all">{bindResult.tenant}</code>
                                    {bindResult.expires_in_sec && <> · token TTL: {bindResult.expires_in_sec}s</>}
                                  </p>
                                )}
                                {bindResult.ok && bindResult.instance && (
                                  <p className="mt-1 text-[0.65rem] opacity-80">
                                    Instance: <code className="break-all">{bindResult.instance}</code>
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* SCIM Provisioning — render only when at least one source is configured */}
        {!loading && sources.length > 0 && (
          <ScimProvisioningCard api={api} role={user?.role} />
        )}

        {/* Last sync result */}
        {lastResult && (
          <Card className="mt-6 border-emerald-200" data-testid="federation-last-result">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                Last sync result
                <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[0.65rem] ml-1">
                  target = {lastResult.target}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(lastResult.summary || {}).length === 0 ? (
                <p className="text-sm text-slate-500">No source returned any records.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                        <th className="py-2 pr-3">Source</th>
                        <th className="py-2 px-3">Fetched</th>
                        <th className="py-2 px-3">Created</th>
                        <th className="py-2 px-3">Updated</th>
                        <th className="py-2 px-3">Unchanged</th>
                        <th className="py-2 pl-3">Skipped</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(lastResult.summary).map(([sid, c]) => (
                        <tr key={sid} className="border-b border-slate-100" data-testid={`federation-result-${sid}`}>
                          <td className="py-2 pr-3 font-semibold text-slate-700">{sid}</td>
                          <td className="py-2 px-3">{c.fetched ?? 0}</td>
                          <td className="py-2 px-3 text-emerald-700 font-semibold">{c.created ?? 0}</td>
                          <td className="py-2 px-3 text-sky-700 font-semibold">{c.updated ?? 0}</td>
                          <td className="py-2 px-3 text-slate-500">{c.unchanged ?? 0}</td>
                          <td className="py-2 pl-3 text-amber-700">{c.skip ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
